/**
 * Empêche les traducteurs de navigateur de faire planter l'application.
 *
 * Google Translate remplace chaque nœud texte par sa traduction enveloppée
 * dans deux couches de <font>. React, lui, garde une référence vers le nœud
 * d'origine : au premier rendu qui touche ce texte, il demande au DOM
 * d'insérer un élément avant un nœud qui n'est plus là, ou d'en retirer un
 * qui a changé de parent. Le navigateur lève alors un NotFoundError, que
 * React ne rattrape pas — et toute l'interface meurt.
 *
 * Reproduit le 23 août 2026 sur /en/manager : traduire la page en français,
 * puis provoquer un rendu, suffit à faire tomber l'écran entier. Un client
 * l'avait rencontré la veille sans rien signaler, il voyait une trace
 * JavaScript sur fond noir.
 *
 * Le correctif ne masque pas un défaut de notre code : en fonctionnement
 * normal, React ne passe JAMAIS à ces deux méthodes un nœud dont le parent
 * ne correspond pas. Quand le cas se présente, c'est qu'un tiers a déplacé
 * le DOM sous nos pieds. On ignore alors l'opération plutôt que de laisser
 * l'exception remonter : le texte traduit reste affiché tel quel, ce qui est
 * infiniment préférable à une page blanche.
 *
 * L'alternative — <meta name="google" content="notranslate"> — retirerait la
 * traduction à tout le monde. Nos clients ne sont pas tous francophones ou
 * anglophones : celui qui a rencontré le bug se connecte depuis le Pakistan.
 * Autant lui laisser son outil.
 *
 * À appeler avant le montage de React.
 */
export function protegerDomTraduction(): void {
  if (typeof Node !== 'function' || !Node.prototype) return

  const insertBeforeOriginal = Node.prototype.insertBefore
  Node.prototype.insertBefore = function <T extends Node>(
    nouveauNoeud: T,
    noeudReference: Node | null,
  ): T {
    if (noeudReference && noeudReference.parentNode !== this) {
      // Le nœud de référence a été déplacé ou remplacé par un traducteur.
      // On renvoie le nœud sans l'insérer : React poursuit son rendu.
      return nouveauNoeud
    }
    return insertBeforeOriginal.call(this, nouveauNoeud, noeudReference) as T
  }

  const removeChildOriginal = Node.prototype.removeChild
  Node.prototype.removeChild = function <T extends Node>(enfant: T): T {
    if (enfant.parentNode !== this) {
      // Déjà retiré, ou réparenté par le traducteur : rien à faire.
      return enfant
    }
    return removeChildOriginal.call(this, enfant) as T
  }
}
